import { ThemeEvents } from '@theme/events';

/*
 * Maynila cart drawer
 * - <ma-delivery-estimate>: "Estimated delivery: Nov 6 to Nov 13." from business-day ranges per country.
 *   Counts from the next business day after today, skips weekends and US federal holidays (observed dates),
 *   and adds a peak-season buffer while today is inside the peak window.
 * - Announces quantity changes and removals to screen readers.
 * - Keeps Tab focus inside the open drawer.
 *
 * Horizon fires cart:update BEFORE it morphs the drawer HTML, so the estimate re-renders
 * whenever the morph resets it (MutationObserver), not on the event itself.
 */

/** @param {number} year @param {number} month 0-11 @param {number} weekday 0-6 @param {number} n 1-based, -1 = last */
function nthWeekday(year, month, weekday, n) {
  if (n === -1) {
    const d = new Date(year, month + 1, 0);
    while (d.getDay() !== weekday) d.setDate(d.getDate() - 1);
    return d;
  }
  const d = new Date(year, month, 1);
  while (d.getDay() !== weekday) d.setDate(d.getDate() + 1);
  d.setDate(d.getDate() + (n - 1) * 7);
  return d;
}

/** Fixed-date holidays move to Friday when on Saturday, Monday when on Sunday. */
function observed(d) {
  if (d.getDay() === 6) d.setDate(d.getDate() - 1);
  else if (d.getDay() === 0) d.setDate(d.getDate() + 1);
  return d;
}

const holidayCache = new Map();

/** @param {number} year @returns {Set<string>} */
function usFederalHolidays(year) {
  if (holidayCache.has(year)) return holidayCache.get(year);
  const days = [
    observed(new Date(year, 0, 1)), // New Year's Day
    nthWeekday(year, 0, 1, 3), // Martin Luther King Jr. Day
    nthWeekday(year, 1, 1, 3), // Washington's Birthday
    nthWeekday(year, 4, 1, -1), // Memorial Day
    observed(new Date(year, 5, 19)), // Juneteenth
    observed(new Date(year, 6, 4)), // Independence Day
    nthWeekday(year, 8, 1, 1), // Labor Day
    nthWeekday(year, 9, 1, 2), // Columbus Day
    observed(new Date(year, 10, 11)), // Veterans Day
    nthWeekday(year, 10, 4, 4), // Thanksgiving
    observed(new Date(year, 11, 25)), // Christmas
  ];
  // New Year's Day of next year can be observed on Dec 31 of this year
  const nextNewYear = observed(new Date(year + 1, 0, 1));
  if (nextNewYear.getFullYear() === year) days.push(nextNewYear);
  const set = new Set(days.map(key));
  holidayCache.set(year, set);
  return set;
}

/** @param {Date} d */
function key(d) {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** @param {Date} d */
function isBusinessDay(d) {
  const day = d.getDay();
  return day !== 0 && day !== 6 && !usFederalHolidays(d.getFullYear()).has(key(d));
}

/** Business day number n after `from` (n = 1 is the next business day after `from`). */
export function addBusinessDays(from, n) {
  const d = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  let counted = 0;
  while (counted < n) {
    d.setDate(d.getDate() + 1);
    if (isBusinessDay(d)) counted++;
  }
  return d;
}

/** @param {Date} today @param {string} start "MM-DD" @param {string} end "MM-DD" */
function inPeak(today, start, end) {
  const parse = (s) => {
    const [m, d] = String(s || '').split('-').map(Number);
    return m && d ? m * 100 + d : null;
  };
  const s = parse(start);
  const e = parse(end);
  if (s === null || e === null) return false;
  const t = (today.getMonth() + 1) * 100 + today.getDate();
  return s <= e ? t >= s && t <= e : t >= s || t <= e;
}

const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });

class MaDeliveryEstimate extends HTMLElement {
  #observer = new MutationObserver(() => this.render());

  connectedCallback() {
    this.render();
    this.#observer.observe(this, { attributes: true, childList: true, characterData: true, subtree: true });
  }

  disconnectedCallback() {
    this.#observer.disconnect();
  }

  /** @returns {string | null} */
  text() {
    let ranges = {};
    try {
      ranges = JSON.parse(this.dataset.ranges || '{}');
    } catch (e) {
      return null;
    }
    const range = ranges[this.dataset.country || ''];
    if (!Array.isArray(range)) return null;
    let [min, max] = range.map(Number);
    if (!(min > 0) || !(max >= min)) return null;

    const today = new Date();
    const buffer = Number(this.dataset.buffer) || 0;
    if (buffer > 0 && inPeak(today, this.dataset.peakStart, this.dataset.peakEnd)) {
      min += buffer;
      max += buffer;
    }
    const template = this.dataset.template || '';
    return template
      .replace('[start]', formatter.format(addBusinessDays(today, min)))
      .replace('[end]', formatter.format(addBusinessDays(today, max)));
  }

  render() {
    const text = this.text();
    if (!text) {
      if (!this.hidden) this.hidden = true;
      return;
    }
    if (this.textContent !== text) this.textContent = text;
    if (this.hidden) this.hidden = false;
  }
}

if (!customElements.get('ma-delivery-estimate')) {
  customElements.define('ma-delivery-estimate', MaDeliveryEstimate);
}

/* Screen-reader announcement after quantity changes and removals (Horizon only announces adds). */
document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const detail = /** @type {CustomEvent} */ (event).detail;
  if (detail?.data?.source !== 'cart-items-component') return;
  const dialog = document.querySelector('.cart-drawer__dialog');
  if (!dialog?.open) return;
  const count = detail.data.itemCount;
  // Wait for Horizon's morph so the total in the DOM is the new one
  setTimeout(() => {
    const live = dialog.querySelector('[ref="liveRegion"]');
    if (!live) return;
    const heading = dialog.querySelector('#cart-drawer-heading, #cart-drawer-heading-empty');
    const total = dialog.querySelector('[data-testid="cart-total-value"]');
    const parts = [heading?.textContent?.trim()];
    if (count > 0 && total) parts.push(total.textContent?.trim());
    live.textContent = parts.filter(Boolean).join('. ');
  }, 400);
});

/*
 * Keep Tab inside the open drawer instead of escaping to the browser UI.
 * Focus sentinels at both ends of the dialog, so Shopify's wallet buttons
 * (inside a shadow root) stay in the natural tab order.
 */
const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

/** @param {Element} dialog */
function focusables(dialog) {
  return Array.from(dialog.querySelectorAll(FOCUSABLE)).filter(
    (el) =>
      el instanceof HTMLElement &&
      !el.classList.contains('ma-focus-sentinel') &&
      el.getClientRects().length > 0 &&
      !el.closest('[hidden], [inert]')
  );
}

/** @param {Element} dialog */
function ensureSentinels(dialog) {
  if (dialog.querySelector(':scope > .ma-focus-sentinel')) return;
  const make = (pos) => {
    const s = document.createElement('span');
    s.tabIndex = 0;
    s.className = 'ma-focus-sentinel';
    s.dataset.pos = pos;
    return s;
  };
  dialog.prepend(make('start'));
  dialog.append(make('end'));
}

document.addEventListener('focusin', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  const dialog = target.closest('.cart-drawer__dialog');
  if (!dialog) return;
  ensureSentinels(dialog);
  if (!target.classList.contains('ma-focus-sentinel')) return;
  const list = focusables(dialog);
  const next = target.dataset.pos === 'end' ? list[0] : list[list.length - 1];
  next?.focus();
});
